import { makeAutoObservable } from 'mobx';

interface IUserSettings {
  showPages: boolean;
  gridSnapOnResize: boolean;
  gridVisible: boolean;
  gridSizePx: number;
}

interface ISnapBoundaryCache {
  original: number;
  snapped: number;
  axis: 'x' | 'y';
  timestamp: number;
}

export default class UserSettingStore {
  userSettings: IUserSettings;
  // Cache for snapped boundary positions to ensure sibling containers snap to the same grid line
  private snapBoundaryCache: ISnapBoundaryCache[] = [];
  private snapCacheTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    makeAutoObservable(this);

    const storedSettings = localStorage.getItem('creatorUserSettings');

    const defaultSettings: IUserSettings = {
      showPages: false,
      gridSnapOnResize: false,
      gridVisible: false,
      gridSizePx: 20,
    };

    let parsed: Partial<IUserSettings> = {};
    if (storedSettings) {
      try {
        parsed = JSON.parse(storedSettings) as Partial<IUserSettings>;
      } catch {
        parsed = {};
      }
    }

    const merged = { ...defaultSettings, ...parsed } as IUserSettings;

    // Basic type-safety for backwards compatibility (older localStorage entries).
    merged.showPages = typeof merged.showPages === 'boolean' ? merged.showPages : defaultSettings.showPages;
    merged.gridSnapOnResize = typeof merged.gridSnapOnResize === 'boolean' ? merged.gridSnapOnResize : defaultSettings.gridSnapOnResize;
    merged.gridVisible = typeof merged.gridVisible === 'boolean' ? merged.gridVisible : defaultSettings.gridVisible;
    merged.gridSizePx = Number.isFinite(merged.gridSizePx) && merged.gridSizePx > 0 ? merged.gridSizePx : defaultSettings.gridSizePx;

    this.userSettings = merged;
  }

  setSetting = <K extends keyof IUserSettings>(key: K, value: IUserSettings[K]) => {
    this.userSettings[key] = value;
    localStorage.setItem('creatorUserSettings', JSON.stringify(this.userSettings));
  };

  // Store a snapped boundary position so sibling containers can use the same value
  cacheSnappedBoundary = (original: number, snapped: number, axis: 'x' | 'y') => {
    const now = Date.now();
    this.snapBoundaryCache.push({ original, snapped, axis, timestamp: now });

    if (this.snapCacheTimeout) {
      clearTimeout(this.snapCacheTimeout);
    }
    this.snapCacheTimeout = setTimeout(() => {
      this.snapBoundaryCache = [];
      this.snapCacheTimeout = null;
    }, 50);
  };

  // Get a cached snapped boundary if the position is close to one that was recently snapped
  getCachedSnappedBoundary = (position: number, axis: 'x' | 'y', tolerance: number = 5): number | null => {
    const now = Date.now();
    for (const cache of this.snapBoundaryCache) {
      // Only use cache entries from the same axis and within the last 50ms
      if (cache.axis === axis && now - cache.timestamp < 50) {
        if (Math.abs(position - cache.original) <= tolerance) {
          return cache.snapped;
        }
        if (Math.abs(position - cache.snapped) <= tolerance) {
          return cache.snapped;
        }
      }
    }
    return null;
  };
}
